import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import {getFieldArrayFromObjectArrayByField, isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {loanLibraryQueueApi} from "./loanLibraryQueueApi";
import {orderRequestApi} from "../../../Order/OrderRequest/orderRequestApi";
import {createListFromArrayOfObjects} from "../../../../Utilities/Types/listUtilities";
import type { ListItem } from "../../../../Utilities/Types/listUtilities";
import {PageTitle} from "../../../../Components/Landmarks";
import {LayoutFullBleed} from "../../../../Components/Layout";
import {LoanLibraryQueueForm} from "./LoanLibraryQueueForm";
import {emptyLoanLibraryQueueModel, queueItemModel} from "./loanLibraryQueueFactory";
import {useInputForm} from "../../../../Components/Inputs";
import {
    createConfirmationMessageForDuplicates,
    filterQueueByStatus,
    getAllDuplicateCatalogItemsForStudent,
} from "./loanLibraryQueueUtility";
import {LoanLibraryQueueList} from "./LoanLibraryQueueList";
import {GridCell} from "../../../../Components/Grid";
import {SYSTEM_TYPES} from "../../libraryConstants";
import {getSystemTypeTextByValue} from "../../libraryUtility";

export const LoanLibraryQueueContainer = ({
                                              handleApiCall,
                                              setWarnings,
                                          }: ContextProps) => {

    const [loanLibraryQueue, setLoanLibraryQueue] = useState<Record<string, unknown>[]>([]);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set([]));
    const [shippingLocations, setShippingLocations] = useState<ListItem[]>();

    const queueForm = useInputForm(emptyLoanLibraryQueueModel);

    const pendingQueue = filterQueueByStatus(loanLibraryQueue, true);
    const isPendingQueueEmpty = isArrayNullOrEmpty(pendingQueue);
    const processingQueue = filterQueueByStatus(loanLibraryQueue, false);

    const handleSelectAll = () => {
        const queueItems = getFieldArrayFromObjectArrayByField(pendingQueue, "loanLibraryQueueId");
        setSelectedItems(new Set(queueItems.map((loanLibraryQueueId: unknown) => String(loanLibraryQueueId))));
    }

    const handleDeselectAll = () => {
        setSelectedItems(new Set([]));    
    }

    const handleSelectedItemsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const item = String(event.target.value);
        const updatedSet = new Set(selectedItems || []);

        updatedSet.has(item)
            ? updatedSet.delete(item)
            : updatedSet.add(item);

        setSelectedItems(updatedSet);
    }

    const handleGetQueue = () => {
        handleApiCall(() => loanLibraryQueueApi.getUserQueue(), (results: Record<string, unknown>[]) => {
            const transformedResults = Array.isArray(results) 
                ? results.map((item: Record<string, unknown>) => queueItemModel(item))
                : [];
            setLoanLibraryQueue(transformedResults);
        });
    }

    const handleRemoveFromQueue = (loanLibraryQueueItemId: string) => {
        if (confirm(`This will remove PERMANENTLY your request from the Queue. Are you sure you want to continue?`))
            handleApiCall(() => loanLibraryQueueApi.deleteQueueItem(loanLibraryQueueItemId), handleGetQueue);
    }

    const handleAssignQueueItems = async (loanLibraryQueueViewModel: Record<string, unknown>) => {
        const capturedSelectedItems = Array.from(selectedItems);
        
        loanLibraryQueueViewModel.loanLibraryQueueItemIds = capturedSelectedItems;

        const noItemsSelectedFromPendingQueue = isArrayNullOrEmpty(loanLibraryQueueViewModel.loanLibraryQueueItemIds);
        if(noItemsSelectedFromPendingQueue) {
            setWarnings(["Please select at least one item from the queue to assign."]);
            return;
        }

        const duplicateCatalogItems = getAllDuplicateCatalogItemsForStudent(loanLibraryQueueViewModel, pendingQueue, processingQueue);
        const hasDuplicates = duplicateCatalogItems.length > 0;
        const confirmationMessage = createConfirmationMessageForDuplicates(duplicateCatalogItems, hasDuplicates);

        if (!hasDuplicates || (hasDuplicates && confirm(confirmationMessage))) {
            handleApiCall(() => loanLibraryQueueApi.assignShippingLocation(loanLibraryQueueViewModel), handleGetQueue);
            setSelectedItems(new Set([]));
        }
    }

    useEffect(() => {

        const getShippingLocations = () => {
            handleApiCall(() => orderRequestApi.getShippingLocations(), (results: Record<string, unknown>[]) => {
                setShippingLocations(createListFromArrayOfObjects(results, `shippingLocationId`, `name`));
            });
        }

        if (!shippingLocations)
            getShippingLocations();

        handleGetQueue();
    }, []);

    if (!shippingLocations || !queueForm) return;

    return <>
        <PageTitle h1={`${getSystemTypeTextByValue(SYSTEM_TYPES.LoanLibrary.toString())} Queue`}/>

        <GridCell>
            <p>
                Please check with your school/organization to confirm that someone will be there to accept your
                packages. Items may take time to arrive. All packages not received by your school or
                organization will be returned.
            </p>
        </GridCell>

        <LayoutFullBleed className={`catalog`}>
            {
                pendingQueue &&
                <LoanLibraryQueueList
                    queueItems={pendingQueue}
                    handleDeselectAll={handleDeselectAll}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    handleSelectAll={handleSelectAll}
                    handleSelectedItemsChange={handleSelectedItemsChange}
                    selectedItems={selectedItems}
                />
            }
            {
                !isPendingQueueEmpty &&
                <LoanLibraryQueueForm
                    queueForm={queueForm}
                    handleAssignQueueItems={handleAssignQueueItems}
                    setWarnings={setWarnings}
                    shippingLocations={shippingLocations}
                />
            }
            {
                processingQueue &&
                <LoanLibraryQueueList
                    queueItems={processingQueue}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    isPendingQueue={false}
                />
            }
        </LayoutFullBleed>
    </>;
};

